import { Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { ReturnTracker, TodoPanel } from "../PortalSections";
import { DeadlineCalendar } from "../../DeadlineCalendar";
import { AIAutomationStatus, InternationalComplianceHub, RealEstateAssetTracker, RequestServiceCta } from "../DashboardWidgets";
import DemographicsBanner from "../DemographicsBanner";

// The Overview tab is the daily-check-in surface: return status, todos, deadlines,
// widgets that surface less-discovered services, plus the encrypted-chat composer.
export default function OverviewTab({
  data, extras, messages, unread,
  pendingEnvelopes, onTodoAction, setFinalEng, setIntlOpen, setExchOpen,
  msg, setMsg, msgDocTag, setMsgDocTag, sendMsg,
}) {
  return (
    <div className="space-y-6" data-testid="portal-panel-overview">
      <DemographicsBanner />
      {pendingEnvelopes.length > 0 && (
        <div data-testid="final-flow-banner" className="rounded-lg bg-zinc-900 text-white p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium flex items-center gap-2"><Sparkle size={16} /> Your return is ready for final steps</p>
            <p className="text-xs text-zinc-300 mt-1">Review your plain-English summary, settle the invoice, and sign — all in one flow.</p>
          </div>
          <Button data-testid="final-flow-open-btn" variant="secondary" size="sm" className="shrink-0" onClick={() => setFinalEng(pendingEnvelopes[0].engagement_id)}>Review & finish →</Button>
        </div>
      )}
      <ReturnTracker tracker={extras?.tracker} refundLinks={extras?.refund_links} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TodoPanel todos={extras?.todos} onAction={onTodoAction} />
        <DeadlineCalendar compact />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <AIAutomationStatus documents={data?.documents} />
        <InternationalComplianceHub onOpen={() => setIntlOpen(true)} />
        <RealEstateAssetTracker onOpen={() => setExchOpen(true)} />
      </div>
      <RequestServiceCta />

      <div id="portal-messages" className="rounded-lg border border-zinc-200 bg-white p-5 flex flex-col scroll-mt-16 hover:shadow-md transition-shadow duration-200" data-testid="portal-messages-block">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-900">Encrypted chat with your firm</h3>
          {(unread?.unread || 0) > 0 && <span className="text-[10px] bg-rose-500 text-white rounded-full px-2 py-0.5">{unread.unread} new</span>}
        </div>
        <div className="flex-1 space-y-3 max-h-[280px] overflow-y-auto mb-3">
          {(messages || []).slice(-10).map((m) => (
            <div key={m.id} className={`max-w-[85%] ${m.sender_role === "client" ? "ml-auto" : ""}`}>
              <p className={`text-sm rounded-md px-3 py-2 whitespace-pre-wrap ${m.sender_role === "client" ? "bg-slate-900 text-white" : "bg-zinc-100 text-zinc-800"}`}>{m.body}</p>
              <p className="text-[10px] text-zinc-400 mt-1">{m.sender_name}</p>
            </div>
          ))}
          {!messages?.length && <p className="text-xs text-zinc-400 italic">No messages yet — send a hello to your CPA.</p>}
        </div>
        {(data?.documents || []).length > 0 && (
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            {msgDocTag ? (
              <span data-testid="msg-doc-tag" className="inline-flex items-center gap-1.5 text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full pl-2 pr-1 py-0.5">
                Re: <span className="font-mono">{msgDocTag.filename.slice(0, 32)}{msgDocTag.filename.length > 32 ? "…" : ""}</span>
                <button data-testid="msg-doc-tag-clear" onClick={() => setMsgDocTag(null)} className="ml-0.5 w-4 h-4 rounded-full hover:bg-emerald-100 text-emerald-800">×</button>
              </span>
            ) : (
              <select
                data-testid="msg-doc-tag-select"
                onChange={(e) => {
                  const d = (data.documents || []).find((x) => x.id === e.target.value);
                  if (d) setMsgDocTag(d);
                  e.target.value = "";
                }}
                defaultValue=""
                className="text-[10px] text-slate-600 bg-zinc-50 border border-zinc-200 rounded-full px-2 py-0.5 hover:bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-slate-900">
                <option value="" disabled>+ Attach a document reference</option>
                {(data.documents || []).slice(0, 20).map((d) => (
                  <option key={d.id} value={d.id}>{d.filename}</option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Input data-testid="portal-message-input" placeholder={msgDocTag ? `Message about ${msgDocTag.filename}…` : "Message your CPA…"} value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && msg && sendMsg.mutate()} />
          <Button data-testid="portal-message-send-btn" disabled={!msg || sendMsg.isPending} onClick={() => sendMsg.mutate()}><PaperPlaneTilt size={14} /></Button>
        </div>
      </div>
    </div>
  );
}
